import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Plane, AlertTriangle, MessageSquareWarning, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";
import DailyInspectionScan from "@/pages/DailyInspectionScan";

const AC_STATUS = {
  normal: { label: "Serviceable", cls: "bg-emerald-100 text-emerald-800", plane: "text-emerald-500" },
  heavy_check: { label: "Heavy Check", cls: "bg-blue-100 text-blue-800", plane: "text-blue-500" },
  mel: { label: "MEL", cls: "bg-amber-100 text-amber-800", plane: "text-amber-500" },
  capex: { label: "CAPEX", cls: "bg-rose-100 text-rose-800", plane: "text-rose-500" },
  unserviceable: { label: "Unserviceable", cls: "bg-red-100 text-red-800", plane: "text-red-600" },
  offline: { label: "Offline", cls: "bg-gray-200 text-gray-600", plane: "text-gray-400" },
};
const todayIso = () => new Date().toLocaleDateString("en-CA");
const isOverdue = (d) => !!d && d < todayIso();
const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString("en-CA", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");

export default function MaintScan() {
  const { tail } = useParams();
  const [ac, setAc] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [pin, setPin] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [di, setDi] = useState(null);
  const [diOpen, setDiOpen] = useState(null);
  const [diStarting, setDiStarting] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API_BASE}/maintenance/public/${encodeURIComponent(tail)}`);
      setAc(r.data);
      if (["ATR72", "DORNIER 228", "AVRO RJ85", "AVRO RJ100", "DASH 8"].includes((r.data.fleet || "").toUpperCase())) {
        try {
          const d = await axios.get(`${API_BASE}/maintenance/public/${encodeURIComponent(tail)}/daily-inspections`);
          setDi(d.data);
        } catch { /* ignore */ }
      }
    } catch {
      setNotFound(true);
    }
  }, [tail]);
  useEffect(() => { load(); }, [load]);

  const startInspection = async () => {
    let ident = { name: "", pin: "" };
    try { ident = JSON.parse(localStorage.getItem("sa_di_identity") || "null") || ident; } catch { /* ignore */ }
    if (!ident.name || !/^\d{4}$/.test(ident.pin || "")) {
      const nm = window.prompt("Your name (stamped on the form):", ident.name || "");
      if (!nm || !nm.trim()) return;
      const pn = window.prompt("Aircraft 4-digit QR PIN:", "");
      if (!pn || !/^\d{4}$/.test(pn.trim())) { toast.error("A valid 4-digit PIN is required"); return; }
      ident = { name: nm.trim(), pin: pn.trim() };
      try { localStorage.setItem("sa_di_identity", JSON.stringify(ident)); } catch { /* ignore */ }
    }
    setDiStarting(true);
    try {
      const r = await axios.post(`${API_BASE}/maintenance/public/${encodeURIComponent(tail)}/daily-inspections`, ident);
      setDiOpen(r.data.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start inspection");
      if (e?.response?.status === 403) { try { localStorage.removeItem("sa_di_identity"); } catch { /* ignore */ } }
    } finally { setDiStarting(false); }
  };

  const submitReport = async () => {
    if (!name.trim()) { toast.error("Your name is required"); return; }
    if (!/^\d{4}$/.test(pin)) { toast.error("Enter the 4-digit PIN"); return; }
    if (!text.trim()) { toast.error("Describe the problem"); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API_BASE}/maintenance/public/${encodeURIComponent(tail)}/problems`, { pin, name: name.trim(), text: text.trim() });
      setAc(r.data);
      setText("");
      setShowForm(false);
      toast.success("Problem reported — visible on the Maintenance page");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit report");
    } finally {
      setBusy(false);
    }
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-summit-canvas flex items-center justify-center px-4">
        <div className="text-center" data-testid="maint-qr-not-found">
          <Plane className="w-8 h-8 mx-auto text-gray-300" />
          <h1 className="font-display text-xl font-semibold mt-3">Aircraft not found</h1>
          <p className="text-sm text-gray-500 mt-1">This QR code doesn&apos;t match any active tail.</p>
        </div>
      </div>
    );
  }
  if (!ac) return <div className="min-h-screen bg-summit-canvas flex items-center justify-center text-sm text-gray-400">Loading…</div>;

  if (diOpen) {
    return <DailyInspectionScan tail={tail} iid={diOpen} onExit={() => { setDiOpen(null); load(); }} />;
  }

  const st = AC_STATUS[ac.status] || AC_STATUS.normal;
  const problems = ac.problem_reports || [];

  return (
    <div className="min-h-screen bg-summit-canvas px-4 py-6">
      <div className="max-w-md mx-auto">
        <div className="font-mono text-[10px] uppercase tracking-widest text-gray-400 mb-2 flex items-center gap-1.5">
          <Plane className="w-3.5 h-3.5" /> Summit Air · Aircraft Maintenance
        </div>
        <div className="bg-white border border-summit-hairline rounded-lg overflow-hidden" data-testid="maint-qr-card">
          <div className="bg-summit-primary text-white px-4 py-3 flex items-center gap-2.5 flex-wrap">
            <Plane className={`w-4 h-4 shrink-0 ${st.plane}`} fill="currentColor" />
            <span className="font-display font-semibold" data-testid="maint-qr-tail">{ac.tail}</span>
            <span className={`ml-auto px-2 py-0.5 rounded-full font-mono text-[10px] uppercase tracking-widest ${st.cls}`} data-testid="maint-qr-status-badge">{st.label}</span>
          </div>
          <div className="p-4 space-y-1 text-sm">
            <div className="flex justify-between gap-2"><span className="text-gray-400 text-xs">Fleet</span><span>{ac.fleet || "—"}</span></div>
            <div className="flex justify-between gap-2"><span className="text-gray-400 text-xs">Log #</span><span data-testid="maint-qr-log">{ac.log_number ?? "—"}</span></div>
            <div className="flex justify-between gap-2"><span className="text-gray-400 text-xs">Aircraft hours</span><span data-testid="maint-qr-hours">{ac.airframe_hours ?? "—"}</span></div>
            <div className="flex justify-between gap-2"><span className="text-gray-400 text-xs">Cycles</span><span data-testid="maint-qr-cycles">{ac.cycles ?? "—"}</span></div>
            <div className="flex justify-between gap-2">
              <span className="text-gray-400 text-xs">Next calendar due</span>
              <span className={isOverdue(ac.next_calendar_due) ? "text-rose-600 font-medium" : ""} data-testid="maint-qr-caldue">
                {ac.next_calendar_due || "—"}{isOverdue(ac.next_calendar_due) ? " · OVERDUE" : ""}
              </span>
            </div>
            {ac.status_updated_by_name && (
              <div className="text-[11px] text-gray-500 pt-1" data-testid="maint-qr-status-stamp">
                {st.label} since {fmtWhen(ac.status_updated_at)} · {ac.status_updated_by_name}
                {ac.status_note ? <span className="italic"> — “{ac.status_note}”</span> : null}
              </div>
            )}
          </div>
        </div>

        <div className="mt-4 bg-white border border-summit-hairline rounded-lg p-4" data-testid="maint-qr-problems">
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 flex items-center gap-1.5 mb-2">
            <AlertTriangle className="w-3 h-3" /> Open problem reports ({problems.length})
          </div>
          {problems.length === 0 && <p className="text-xs text-gray-400">No open problems.</p>}
          <div className="space-y-2">
            {problems.map((p) => (
              <div key={p.id} className="border border-rose-200 rounded-md px-2.5 py-2 bg-rose-50/50 text-xs" data-testid={`maint-qr-pr-${p.id}`}>
                <div className="whitespace-pre-wrap">{p.text}</div>
                <div className="text-[10px] text-gray-500 mt-1">{p.reported_by_name} · {fmtWhen(p.created_at)}</div>
              </div>
            ))}
          </div>
        </div>

        {["ATR72", "DORNIER 228", "AVRO RJ85", "AVRO RJ100", "DASH 8"].includes((ac.fleet || "").toUpperCase()) && (
          <div className="mt-4 bg-white border border-summit-hairline rounded-lg p-4" data-testid="maint-qr-di">
            <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 flex items-center gap-1.5 mb-2">
              <ClipboardCheck className="w-3 h-3" /> Daily Inspection · {di?.form?.number || ""}
            </div>
            {di?.last_completed ? (
              <p className="text-xs text-gray-600" data-testid="maint-qr-di-last">
                Last completed <b>{fmtWhen(di.last_completed.completed_at)}</b> by {di.last_completed.completed_by_name}
              </p>
            ) : (
              <p className="text-xs text-gray-400" data-testid="maint-qr-di-last">No completed inspection in the last 48 hours.</p>
            )}
            {(di?.active || []).map((a) => (
              <button
                key={a.id}
                className="mt-2 w-full text-left border border-amber-300 bg-amber-50 rounded-md px-3 py-2 text-xs hover:bg-amber-100"
                onClick={() => setDiOpen(a.id)}
                data-testid={`maint-qr-di-resume-${a.id}`}
              >
                <b>In progress</b> — {a.signed_count}/{a.total} signed · started {fmtWhen(a.created_at)} by {a.created_by_name}
                <span className="block text-[10px] text-amber-700 mt-0.5">Tap to continue this inspection</span>
              </button>
            ))}
            <Button
              className="mt-3 w-full bg-summit-primary hover:bg-summit-primary-soft text-white"
              onClick={startInspection}
              disabled={diStarting}
              data-testid="maint-qr-di-start"
            >
              <ClipboardCheck className="w-4 h-4 mr-1.5" /> Start new Daily Inspection
            </Button>
          </div>
        )}

        <Button
          className="mt-4 w-full bg-summit-primary hover:bg-summit-primary-soft text-white"
          onClick={() => setShowForm((v) => !v)}
          data-testid="maint-qr-report-toggle"
        >
          <MessageSquareWarning className="w-4 h-4 mr-1.5" /> Report a problem
        </Button>

        {showForm && (
          <div className="mt-3 bg-white border border-summit-hairline rounded-lg p-4 space-y-3" data-testid="maint-qr-report-form">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-gray-500">Your name *</Label>
                <Input className="mt-1 h-9" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. J. Smith" maxLength={60} data-testid="maint-qr-name" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-gray-500">4-digit PIN *</Label>
                <Input
                  className="mt-1 h-9 w-28 font-mono tracking-[0.3em] text-center"
                  inputMode="numeric"
                  maxLength={4}
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                  placeholder="••••"
                  data-testid="maint-qr-pin"
                />
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-gray-500">What&apos;s wrong? *</Label>
              <Textarea className="mt-1 text-sm" rows={3} value={text} onChange={(e) => setText(e.target.value)} maxLength={1000} placeholder="Describe the problem…" data-testid="maint-qr-text" />
            </div>
            <Button className="w-full bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={submitReport} disabled={busy} data-testid="maint-qr-submit">
              Submit problem report
            </Button>
            <p className="text-[10px] text-gray-400">Reports are stamped with your name, date &amp; time. The PIN is printed on this aircraft&apos;s QR card.</p>
          </div>
        )}
      </div>
    </div>
  );
}
